import React, { useState, createContext, useEffect, useCallback, startTransition } from 'react';
import Visitor from 'src/zapi/audiences/visitor';
import { getCookie, readLocalStorage, updateLocalStorage } from 'utils/storage';
import { STORAGE_KEY, AUDIENCE_WEIGHTS } from 'constants/personalization';
import { graphql, useStaticQuery } from 'gatsby';
import { Location } from '@reach/router';

const PersonalizationContext = createContext(null);

let vistorUpdateTimer;
let visitorUpdateFunc;

 const getHumanReadableWeight = (numericalValue) => {
	for (const [key, [min, max]] of Object.entries(AUDIENCE_WEIGHTS)) {
		if (numericalValue >= min && (max === undefined || numericalValue <= max)) {
			return key;
		}
	}
	return null;
}

const PersonalizationProviderComponent = (props) => {

	const { location } = props;
	const utm_campaign = (location && location.search ? new URLSearchParams(location.search).get('utm_campaign') : null);

	// Initialize with consistent state for both SSR and client
	const [visitor, setVisitor] = useState(null);
	const [isHydrated, setIsHydrated] = useState(false);

	// Move localStorage reads to useEffect to prevent SSR/client mismatch
	useEffect(() => {
		if (typeof window === 'undefined') return; // Only run on client-side
		
		let data = readLocalStorage(STORAGE_KEY);
		let allowed = true;
		const cookieYes = getCookie('cookieyes-consent');
		if (!!cookieYes !== false) {
			const consent = cookieYes.split(',').find(value => value.startsWith('consent:'))?.split(':');
			const analytics = cookieYes.split(',').find(value => value.startsWith('analytics:'))?.split(':');
			if ((!!consent !== false && consent[1] === 'no') || (!!analytics !== false && analytics[1] === 'no')) {
				allowed = false;
			}
		}
		async function addVisitor(params = {}) {
			const response = await Visitor.add({
				...params,
				googleID: (allowed ? getCookie('_ga') : null),
				permitted: allowed,
			});
			if (!!response === false)return;
			if (response.data && response.data.length > 0){
				const data = {
					id: response.data[0].id,
					Audience: response.data[0].Audience,
					audiences: response.data[0].audiences,
					googleID: response.data[0].googleID,
					permitted: response.data[0].permitted,
					sessions: response.data[0].sessions,
				};
				updateLocalStorage(STORAGE_KEY, {...data});
				startTransition(() => {
					setVisitor(data);
				});
			}
			startTransition(() => {
				setIsHydrated(true);
			});
		}
		if (!!data === false) {
			addVisitor({
				sessions: [{
					id: Date.now(),
					created: new Date(),
					counter: 1,
					events: [],
				}],
			});
		} else {
			// Loop through and remove old sessions (past 1 year)
			// If the most recent session is older than 20 minutes, start a new one.
			if (data.permitted !== allowed)data.permitted = allowed;
			const now = Date.now();
			const expiration = 1000*60*20;
			const lastYear = new Date();
			lastYear.setFullYear(lastYear.getFullYear()-1);
			const lastYearTS = lastYear.getTime();
			if (!!data.sessions === false || data.sessions.length === 0) {
				data.sessions = [{
					id: Date.now(),
					created: new Date(),
					counter: 1,
					events: [],
				}];
			} else {
				data.sessions = data.sessions.filter((session) => {
					const ts = Date.parse(session.created);
					return ts > lastYearTS;
				});
				const recent = (data.sessions[data.sessions.length-1].lastSeen ? Date.parse(data.sessions[data.sessions.length-1].lastSeen) : Date.parse(data.sessions[data.sessions.length-1].created));
				if (now - recent > expiration) {
					data.sessions.push({
						id: Date.now(),
						created: new Date(),
						counter: 1,
						events: [],
					});
				} else {
					data.sessions[data.sessions.length-1].counter++;
					data.sessions[data.sessions.length-1].lastSeen = new Date();
				}
			}
			updateLocalStorage(STORAGE_KEY, {...data});
			startTransition(() => {
				setVisitor(data);
				setIsHydrated(true);
			});
		}
	}, []); // Run once on mount

	const { allAudiencesTrigger } = props.data;
	const triggers = allAudiencesTrigger.nodes.filter(item => {
			if (!utm_campaign) {
				return visitor?.audiences?.[item.Audience[0]?.object?.id] !== undefined;
			} else {
				return item.value === utm_campaign;
			}
		});
	
	const [audience, setAudience] = useState(() => {
		const def = triggers.length > 0 
			? triggers[0].Audience[0]?.object
			: props.data.allAudiencesAudience.nodes.find(item => item.default);
		if (!def)return null;
		const defaultAudience = {
			[def.id]: {
				...def,
				weight: triggers.length > 0 ? getHumanReadableWeight(triggers[0].score) : def.defaultWeight,
				score: AUDIENCE_WEIGHTS[def.defaultWeight][0],
			}
		}
		return defaultAudience;
	});

	const isSameAudience = (...audiences) => {
		let hash = null;
		for (let check of audiences) {
			if (!!check === false)continue;
			let current = '';
			const keys = Object.keys(check).sort();
			for (let key of keys) {
				current += key;
				current += check[key].score;
				current += check[key].weight;
			}
			if (hash === null)hash = current;
			if (hash !== current)return false;
		}
		return true;
	}

	/** 
	 * Loops through the personalization storage object session events and calculates the total score for each audience.
	 * 
	 */
	const calculate = useCallback(() => {
		const scorecard = {};
		const now = (new Date()).getTime();
		const data = readLocalStorage(STORAGE_KEY);
		if (!!data === false)return;
		if (!data.permitted)return;
		let events = [];
		if (data.sessions) {
			data.sessions.forEach(session => {
				if (session.events) {
					const used = [];
					const pure = session.events.filter((event) => {
						if (used.indexOf(event.id) !== -1)return false;
						if (event.lifespan && event.ts+(event.lifespan*24*60*60*1000) < now)return false;
						used.push(event.id);
						return true;
					});
					events = events.concat(pure);
				}
			});
		}
		const sets = events.filter(event => event.action === 'set' && !!event.audiences !== false);
		if (sets.length > 0) {
			// Set the audience specifically, but only the most recent
			sets.sort((left, right) => right.ts - left.ts);
			const event = sets[0];
			event.audiences.forEach((audience) => {
				if (scorecard[audience.id] === undefined){
					scorecard[audience.id] = {
						...audience,
						score: 0,
					}
				};
				scorecard[audience.id].score = event.value;
			});
		} else {
			events.forEach(event => {
				if (!!event.audiences === false)return;
				event.audiences.forEach((audience) => {
					if (scorecard[audience.id] === undefined){
						scorecard[audience.id] = {
							...audience,
							score: 0,
						}
					};
					if (event.action === 'add')scorecard[audience.id].score += event.value;
					if (event.action === 'subtract')scorecard[audience.id].score -= event.value;
				});
			});
		}
		let maxWeight = 0;
		for (const key in scorecard) {
			const score = scorecard[key].score;
			for (const weight in AUDIENCE_WEIGHTS) {
				const min = AUDIENCE_WEIGHTS[weight][0];
				const max = AUDIENCE_WEIGHTS[weight][1];
				maxWeight = Math.max(maxWeight, max||min);
				if (min && max && score >= min && score <= max)scorecard[key].weight = weight;
				if (min && !max && score >= min)scorecard[key].weight = weight;
			}
			// We should have at least a low weight in order to set the audience
			if (scorecard[key].weight === undefined)delete scorecard[key];
		}
		if (!isSameAudience(scorecard, audience)) {
			if (Object.keys(scorecard).length !== 0) {
				startTransition(() => {
					setAudience(scorecard);
				});
			}
		}
	}, [audience]);

	const removeTriggerType = (type) => {
		if (typeof window === 'undefined')return;
		if (!!visitor === false)return;
		const session = visitor.sessions[visitor.sessions.length-1];
		session.events = session.events.filter((item) => item.type !== type); // Keep only the events triggered by other types.
		let pastSessions = readLocalStorage(STORAGE_KEY)?.sessions;
		if (!!pastSessions === false)pastSessions = [session]; // Make sure we add the current session
		for (let i=0; i<pastSessions.length; i++) {
			if (pastSessions[i].id === session.id)pastSessions[i] = {...session};
		}
		const {next} = updateLocalStorage(STORAGE_KEY, {sessions: pastSessions});
		if (vistorUpdateTimer)clearTimeout(vistorUpdateTimer);
		visitorUpdateFunc = () => {
			clearTimeout(vistorUpdateTimer);
			vistorUpdateTimer = null;
			Visitor.update(next);
		}
		vistorUpdateTimer = setTimeout(visitorUpdateFunc, 3000);
		calculate();
	}

	/**
	 * Accepts a trigger object and creates a record of a score event on the current session. The score events are used later to determine audiences.
	 * Parameters are passed as an objects and destructured by the function. Passing a trigger object directly is the most appropriate usage.
	 * 
	 * @param {string} id - The ID of trigger.
	 * @param {string} type - The type of the trigger which was fired to score this event
	 * @param {string} action - Either add, subtract, or set. Add / subtract are simply applied mathematically. Set takes precedence, and a set trigger will be prioritized over others.
	 * @param {number} lifespan - The number of days that this event will be considered for scoring.
	 * @param {number} score - The score value to use for the addition, subtraction, or setting of this event.
	 * @param {[Audience]} Audience - An audience relationship to apply the score to.
	 * 
	 */
	const score = ({id, type, action, lifespan, score: points, overwrite = false, Audience}) => {
		if (typeof window === 'undefined')return; // No trigger should actually fire without the browser
		if (!!visitor === false) { // If we haven't established our visitor context, store the score event in a queue
			window.z_personalization_score_queue = window.z_personalization_score_queue||[];
			window.z_personalization_score_queue.push(() => score({id, type, action, lifespan, score: points, Audience}));
			return;
		}
		if (!visitor.permitted)return false;
		const audiences = Audience.map(({ object }) => object);
		const event = {
			ts: (new Date()).getTime(),
			type: type,
			id: id,
			value: points,
			lifespan: lifespan,
			action: action,
			audiences: audiences,
			overwrite: overwrite,
		};
		const session = visitor.sessions[visitor.sessions.length-1];
		if (event.overwrite) { // This trigger overwrites any previous ones in this session.
			session.events = session.events.filter((item) => item.type !== type);
		}
		const record = session.events.find(item => !!id && item.id === id);
		if (!!record !== false)return; // We don't record the same trigger multiple times per session.
		session.events.push(event);
		let pastSessions = readLocalStorage(STORAGE_KEY)?.sessions;
		if (!!pastSessions === false)pastSessions = [session]; // Make sure we add the current session
		for (let i=0; i<pastSessions.length; i++) {
			if (pastSessions[i].id === session.id)pastSessions[i] = {...session};
		}
		const {next} = updateLocalStorage(STORAGE_KEY, {sessions: pastSessions});
		if (vistorUpdateTimer)clearTimeout(vistorUpdateTimer);
		visitorUpdateFunc = () => {
			clearTimeout(vistorUpdateTimer);
			vistorUpdateTimer = null;
			Visitor.update(next);
		}
		vistorUpdateTimer = setTimeout(visitorUpdateFunc, 3000);
		calculate();
	}

	const getAudience = () => {
		const data = readLocalStorage(STORAGE_KEY);
		let audiences;
		if (!!data === false) {
			audiences = audience;
		} else if (!!data && data?.audiences && Object.keys(data?.audiences).length === 0) {
			audiences = audience;
		} else {
			audiences = data.audiences;
		}
		let primaryID = '';
		let score = 0;
		for (let key in audiences) {
			score = Math.max(audiences[key].score, score);
			if (audiences[key].score === score)primaryID = key;
		}
		return audiences[primaryID];
	}

	/**
	 * Accepts either an audience ID string, an array of audiences, defined below, or a Model object that has an Audience relationship. The Model is the preferred method because it provides more capabilities.
	 * Audience array can be any one of the following
	 *   1. A simple string, which is an Audience ID
	 *   2. An object with a key named "object" which has a key named "id" (what a relationship on a Model typically looks like)
	 *   3. An object with a key named "audience" which is a string Audience ID
	 *   4. An object with a key named "id" which is a string Audience ID
	 * 
	 * When being passed a Model, this function can inspect the Audience relationship to find the proper audiences.
	 * Additionally this function will look for PersonalizationInclude and PersonalizationExclude relationships and handle those accordingly.
	 * 
	 * Options:
	 *   audienceKey - Change where Audiences are found in the model. Default is Audience.
	 *   includeKey - Change where the Personalization Includes are found in the model. Default is PersonalizationInclude
	 *   excludeKey - Change where the Personalization Excludes are found in the model. Default is PersonalizationExclude
	 * 
	 * @param {object|array} model - Either the model or audiences to match.
	 * @param {object} options - Options to change the default keys for Audience, PersonalizationInclude, and PersonalizationExclude
	 * 
	 */
	const matchAudience = (model, options = {
		audienceKey: 'Audience',
		includeKey: 'PersonalizationInclude',
		excludeKey: 'PersonalizationExclude',
	}) => {
		const findAudienceID = (obj) => {
			let audienceID;
			if (typeof obj === 'string')audienceID = obj;
			if (!audienceID && typeof obj === 'object' && obj.object !== undefined)audienceID = obj.object.id;
			if (!audienceID && typeof obj === 'object' && obj.audience !== undefined && typeof obj.audience === 'string')audienceID = obj.audience;
			if (!audienceID && typeof obj === 'object' && obj.id !== undefined)audienceID = obj.id;
			if (audienceID === undefined && typeof obj === 'object' && typeof obj.audience === 'string')audienceID = obj.audience;
			return audienceID;
		}
		const findWeight = (obj) => {
			let weight;
			if (typeof obj === 'object' && obj.properties?.weight !== undefined)weight = obj.properties.weight;
			if (weight === undefined && obj.weight)weight = obj.weight;
			if (weight === undefined)weight = 'low';
			return weight;
		}
		let found;
		let audiences = [];
		if (!!model === false)return true; // No audiences or model defined match because it is not personalized content
		if (Array.isArray(model)) {
			audiences = model;
		}
		if (typeof model === 'string') {
			audiences = [model];
		}
		if (!Array.isArray(model)){
			audiences = model[options.audienceKey];
			if (audiences.length === 0)found = true; // If no audiences, default to not personalized content; but we'll still check for other rules.
			if (!found) {
				const compareTo = getAudience();
				if (!!compareTo !== false) {
					for (let check of audiences) {
						const matchMode = (check?.properties?.matchMode ?? 'match');
						const audienceID = findAudienceID(check);
						const weight = findWeight(check);
						for (const thisWeight of weight) {
							if (compareTo.id !== audienceID)continue;
							switch (matchMode) {
								case 'match':
									if (compareTo.weight === thisWeight)found = true;
									break;

								case 'matchAtLeast': {
									const compareScore = compareTo.score;
									const boundary = (AUDIENCE_WEIGHTS[thisWeight][0] ?? AUDIENCE_WEIGHTS[thisWeight]);
									if (compareScore >= boundary)found = true;
									break;
								}

								case 'matchAtMost': {
									const compareScore = compareTo.score;
									const boundary = (AUDIENCE_WEIGHTS[thisWeight][1] ?? AUDIENCE_WEIGHTS[thisWeight]);
									if (compareScore <= boundary)found = true;
									break;
								}

								case 'negative':
									if (compareTo.weight === thisWeight)found = false;
									break;

								default:
									if (found === undefined)found = false;
							}
						}
					}
				}
			}
			let excluded = false;
			if (found && model[options.excludeKey]) { // We only both with checking for an Exclude rule if this has already been matched.
				const expanded = model[options.excludeKey].map(item => item.object).filter(item => item.Audience && item.Audience.length); // We can only test against those that have an Audience to match with.
				if (expanded.length) {
					for (const item of expanded) {
						const isMatch = matchAudience(item.Audience);
						if (isMatch){
							excluded = true;
							found = false;
							break;
						}
					}
				}
			}
			if (!found && !excluded && model[options.includeKey]) { // We only bother with checking for an Include rule if this hasn't already been matched.
				const expanded = model[options.includeKey].map(item => item.object).filter(item => item.Audience && item.Audience.length); // We can only test against those that have an Audience to match with.
				if (expanded.length) {
					for (const item of expanded) {
						const isMatch = matchAudience(item.Audience);
						if (isMatch){
							found = true;
							break;
						}
					}
				}
			}			
		} else {
			if (audiences.length === 0)return true; // No audiences defined match because it is not personalized content
			const compareTo = getAudience();
			if (!!compareTo !== false) {
				for (let check of audiences) {
					const audienceID = findAudienceID(check);
					const weight = findWeight(check);
					if (compareTo.id === audienceID && compareTo.weight === weight)found = true;
				}
			}
		}
		if (found)return true;
		return false;
	}

	useEffect(() => {
		if (typeof window === 'undefined' || typeof document === 'undefined')return; // Ensure we have a browser context available
		window.z_personalization_score_queue = window.z_personalization_score_queue||[];
		if (!!visitor === false)return;
		if (window.z_personalization_score_queue && window.z_personalization_score_queue.length > 0) {
			window.z_personalization_score_queue.forEach((item) => item());
		}
		calculate();
	}, [visitor, calculate]);

	useEffect(() => {
		if (typeof window === 'undefined' || typeof document === 'undefined')return; // Ensure we have a browser context available
		if (!!audience === false)return;
		if (!!visitor === false)return;
		const event = new CustomEvent('setAudience', {
			detail: audience,
		});
		window.dispatchEvent(event);
		const {didChange, next} = updateLocalStorage(STORAGE_KEY, {audiences: audience});
		if (didChange) {
			Visitor.update(next);
		}
		return () => {
			Visitor.commit();
		}
	}, [audience, visitor]);

	useEffect(() => {
		return () => {
			if (vistorUpdateTimer) {
				visitorUpdateFunc();
				clearTimeout(vistorUpdateTimer);
				vistorUpdateTimer = null;
				visitorUpdateFunc = null;
			}
		}
	}, []);

	return (
		<PersonalizationContext.Provider value={{audience, visitor, setVisitor, getAudience, setAudience, matchAudience, score, isHydrated, removeTriggerType}}>
			{props.children}
		</PersonalizationContext.Provider>
	);
}

const APIRoutes = {
	visitor: '/api/audiences/visitor/',
}

const PersonalizationProvider = (props) => {

	const data = useStaticQuery(graphql`
		query PersonalizationContext {
		  allAudiencesAudience(filter: {approved: {eq: true}, archived: {eq: false}, deleted: {eq: false}}) {
		    nodes {
		      name
		      id
		      utm_campaign
			  default
			  defaultWeight
		    }
		  }
		  allAudiencesTrigger(filter: {approved: {eq: true}, archived: {eq: false}, deleted: {eq: false}, type: {eq: "utm"}}) {
		    nodes {
		      name
		      id
		      type
			  value
			  score
			  Audience {
				object {
					id
				}
			  }
			}
		  }
		}
	`);
	return (
		<Location>
			{({ location }) => <PersonalizationProviderComponent data={data} {...props} location={location} />}
		</Location>
	)
}

export { PersonalizationContext, PersonalizationProvider, APIRoutes };